/**
 * Formatting utility functions
 * Common formatters for dates, text, and other data
 */

import { formatDistanceToNow } from 'date-fns';

/**
 * Format a date string as relative time (e.g., "2 hours ago", "just now")
 *
 * @param dateString - ISO 8601 date string to format
 * @returns Human-readable relative time string
 */
export function formatRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);

    // Check if date is invalid
    if (isNaN(date.getTime())) {
      return 'just now';
    }

    // Check if date is less than 1 minute ago
    const now = new Date();
    const diffInSeconds = (now.getTime() - date.getTime()) / 1000;

    if (diffInSeconds < 60) {
      return 'just now';
    }

    // Use date-fns for relative formatting
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    // Fallback for any parsing errors
    return 'just now';
  }
}

/**
 * Truncate text to a maximum length with ellipsis
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation (default: 60)
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string | null | undefined, maxLength: number = 60): string {
  // Handle null/undefined
  if (text == null) {
    return '';
  }

  // Handle empty string
  if (text.length === 0) {
    return '';
  }

  // Return as-is if within limit
  if (text.length <= maxLength) {
    return text;
  }

  // Truncate and add ellipsis
  return text.slice(0, maxLength) + '...';
}
